import { MonthlyMode, NthLast, PeriodicMode, QuarterlyMode, Repeat, RepeatKind, YearlyMode } from "../../api/Tasks";

export const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const weekdayShortNames = ["S", "M", "T", "W", "T", "F", "S"];
export const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

export const repeatKinds: { value: RepeatKind; label: string }[] = [
    { value: "", label: "(none)" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
    { value: "periodically", label: "Periodically" },
];

export const nthOptions = [
    { value: 1, label: "1st" },
    { value: 2, label: "2nd" },
    { value: 3, label: "3rd" },
    { value: 4, label: "4th" },
    { value: 5, label: "5th" },
    { value: NthLast, label: "last" },
];

export function nthLabel(nth: number) {
    return nthOptions.find(o => o.value === nth)?.label || "";
}

// seedRepeat fills the fields a newly picked repeat mode needs, using the due date
// so the user sees a sensible default instead of an empty form.
export function seedRepeat(
    repeat: Repeat,
    changes: {
        kind?: RepeatKind;
        monthlyMode?: MonthlyMode;
        quarterlyMode?: QuarterlyMode;
        yearlyMode?: YearlyMode;
        periodicMode?: PeriodicMode;
    },
    dueDate: string | null
): Repeat {
    const base = dueDate ? new Date(dueDate + "T00:00:00") : new Date();
    const next = { ...repeat, ...changes };

    if (!next.weekday) next.weekday = base.getDay();
    if (!next.nth) next.nth = Math.ceil(base.getDate() / 7);
    if (!next.month) next.month = base.getMonth() + 1;
    if (!next.dayOfMonth) next.dayOfMonth = base.getDate();
    if (!next.days) next.days = 7;
    if (next.kind === "weekly" && next.weekdays.length === 0) next.weekdays = [base.getDay()];
    if (next.kind === "monthly" && !next.monthlyMode) next.monthlyMode = "day-of-month";
    if (next.kind === "quarterly" && !next.quarterlyMode) next.quarterlyMode = "day-of-month";
    if (next.kind === "yearly" && !next.yearlyMode) next.yearlyMode = "same-date";
    if (next.kind === "periodically" && !next.periodicMode) next.periodicMode = "days";

    return next;
}

export function describeRepeat(repeat: Repeat): string {
    switch (repeat.kind) {
        case "daily":
            return "Every day";
        case "weekly":
            return "Every " + repeat.weekdays.map(d => weekdayNames[d]).join(", ");
        case "monthly":
            return repeat.monthlyMode === "day-of-month"
                ? `Monthly on day ${repeat.dayOfMonth}`
                : `Monthly on the ${nthLabel(repeat.nth)} ${weekdayNames[repeat.weekday]}`;
        case "quarterly":
            return repeat.quarterlyMode === "day-of-month"
                ? `Quarterly on day ${repeat.dayOfMonth}`
                : `Quarterly on the ${nthLabel(repeat.nth)} ${weekdayNames[repeat.weekday]}`;
        case "yearly":
            return repeat.yearlyMode === "same-date"
                ? "Yearly on the same date"
                : `Yearly on the ${nthLabel(repeat.nth)} ${weekdayNames[repeat.weekday]} of ${monthNames[repeat.month - 1]}`;
        case "periodically":
            return repeat.periodicMode === "days"
                ? `${repeat.days} days after completion`
                : `The ${nthLabel(repeat.nth)} ${weekdayNames[repeat.weekday]} after completion`;
        default:
            return "";
    }
}
